import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import { SelectCore } from '#/modules/commonComponents/form/select';
import ColumnTipHeader from '#/modules/commonComponents/table/ColumnTipHeader';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import {
  formatUnitQtyFloatingLabel,
  maxQtyInUnit,
  useMaterialUnitOptions,
} from '#/modules/imsMgmt/helper/materialUnit';
import { STOCK_NO_POLICY_TIP } from '#/modules/imsMgmt/receipts/stockTransfer/helper/stockNoPolicyTip';
import { isFullTransferQty } from '#/modules/imsMgmt/receipts/stockTransfer/helper/formSerialize';
import { STOCK_NO_POLICY_OPTIONS } from '#/modules/imsMgmt/receipts/stockTransfer/helper/transferStatus';
import { draftGroupHeaderBarClassName } from '#/modules/momMgmt/workOrder/helper/draftGroupHeaderBarClassName';
import { filterDraftGroupsByKeyword } from '#/modules/momMgmt/workOrder/helper/filterDraftGroupsByKeyword';
import {
  OTHER_MATERIAL_GROUP_KEY,
  OTHER_MATERIAL_GROUP_NAME,
} from '#/modules/momMgmt/workOrder/transfer/helper/otherMaterialGroup';

/* 欄序：庫編 →（物料）→ 單位 → 數量 → 庫編設定 → 操作 */
const LINE_GRID_WITH_MTR =
  'grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(7rem,9rem)_minmax(13rem,14rem)_minmax(8rem,1fr)_2.5rem]';
const LINE_GRID_NO_MTR =
  'grid-cols-[8rem_minmax(7rem,9rem)_minmax(13rem,14rem)_minmax(8rem,1fr)_2.5rem]';

const LINES_MIN_W = 'min-w-[46rem]';
const QTY_COLUMN_TIP = '數量上限依所選單位換算；各列輸入框提示含上限與基本可用量。';

function lineHeaderClassName(showMaterialStack) {
  return cn(
    'sticky top-0 z-10 grid w-full items-center gap-2 border-b border-gray-300',
    'bg-gray-100 px-3 py-2 text-xs font-medium text-gray-600',
    showMaterialStack ? LINE_GRID_WITH_MTR : LINE_GRID_NO_MTR,
  );
}

function lineRowClassName(showMaterialStack) {
  return cn(
    'grid w-full cursor-pointer items-center gap-2 px-3 py-2 transition-colors',
    showMaterialStack ? LINE_GRID_WITH_MTR : LINE_GRID_NO_MTR,
    'hover:bg-slate-50',
  );
}

function resolveQtyError({ qtyRaw, qtyMax }) {
  if (qtyRaw === '' || qtyRaw == null) {
    return '請輸入數量';
  }
  const qtyNum = Number(qtyRaw);
  if (!Number.isFinite(qtyNum) || qtyNum < 1) {
    return '數量必須為大於 0 的整數';
  }
  if (qtyMax > 0 && qtyNum > qtyMax) {
    return `數量不可超過上限 ${qtyMax}`;
  }
  return null;
}

function DraftLineHeader({ showMaterialStack }) {
  return (
    <div className={lineHeaderClassName(showMaterialStack)} aria-hidden>
      <span className="min-w-0">庫存編號</span>
      {showMaterialStack ? (
        <span className="min-w-0">物料</span>
      ) : null}
      <span className="min-w-0">單位</span>
      <span className="min-w-0">
        <ColumnTipHeader label="數量" tip={QTY_COLUMN_TIP} />
      </span>
      <span className="min-w-0">
        <ColumnTipHeader label="庫編設定" tip={STOCK_NO_POLICY_TIP} />
      </span>
      <span className="sr-only min-w-0">操作</span>
    </div>
  );
}

DraftLineHeader.propTypes = {
  showMaterialStack: PropTypes.bool,
};

function DraftLineRow({
  line,
  index,
  showMaterialStack,
  onFocusMaterial,
  onRemove,
  onQtyChange,
  onUnitChange,
  onUnitOptionsChange,
  onStockNoPolicyChange,
  groupMtrId,
}) {
  const availableBase = Number(line.availableQty) || 0;
  const mtrId = String(line.mtrId ?? '').trim();
  const initialUnitOptions = Array.isArray(line.unitOptions) && line.unitOptions.length > 0
    ? line.unitOptions
    : null;

  const {
    options: unitOptions,
    selectOptions: unitSelectOptions,
    defaultUnitId,
    isLoading: unitOptionsLoading,
  } = useMaterialUnitOptions({
    mtrId,
    initialOptions: initialUnitOptions,
    enabled: Boolean(mtrId),
  });

  const resolvedUnitOptions = (
    Array.isArray(unitOptions) && unitOptions.length > 0
      ? unitOptions
      : (line.unitOptions ?? [])
  );
  const activeUnitId = String(line.unitId ?? '').trim() || defaultUnitId || '';
  const qtyMax = maxQtyInUnit(availableBase, resolvedUnitOptions, activeUnitId);
  const qtyClampMax = Math.max(qtyMax, 1);
  const qtyRaw = line.qty;
  const qtyNum = Number(qtyRaw) || 0;
  const isFullTransfer = isFullTransferQty(
    qtyNum,
    availableBase,
    resolvedUnitOptions,
    activeUnitId,
  );
  const policy = line.stockNoPolicy || 'TRANSFERRED_KEEP_OLD';
  const qtyTip = formatUnitQtyFloatingLabel({
    availableBase,
    qtyMax,
    tone: 'transfer',
  });
  const qtyError = resolveQtyError({ qtyRaw, qtyMax });
  const qtyId = `wo-transfer-qty-${line.invStockId}-${index}`;
  const qtyErrors = qtyError
    ? { [qtyId]: { message: qtyError } }
    : undefined;
  const unitDisabled = !mtrId || unitOptionsLoading;
  const unitInvalid = Boolean(mtrId)
    && !unitOptionsLoading
    && !String(line.unitId ?? '').trim();
  const unitSelectId = `wo-transfer-unit-${line.invStockId}-${index}`;
  const unitErrors = unitInvalid
    ? { [unitSelectId]: { message: '請選擇單位' } }
    : undefined;

  useEffect(() => {
    if (!mtrId || !defaultUnitId || unitOptionsLoading) {
      return;
    }
    if (String(line.unitId ?? '').trim()) {
      return;
    }
    onUnitChange?.(index, defaultUnitId);
  }, [
    mtrId,
    defaultUnitId,
    unitOptionsLoading,
    line.unitId,
    index,
    onUnitChange,
  ]);

  useEffect(() => {
    if (!Array.isArray(unitOptions) || unitOptions.length === 0) {
      return;
    }
    const prev = line.unitOptions ?? [];
    if (prev.length === unitOptions.length) {
      return;
    }
    onUnitOptionsChange?.(index, unitOptions);
  }, [unitOptions, line.unitOptions, index, onUnitOptionsChange]);

  function handleUnitChange(nextUnitId) {
    onUnitChange?.(index, nextUnitId);
  }

  return (
    <li
      className={lineRowClassName(showMaterialStack)}
      onClick={() => onFocusMaterial?.(groupMtrId)}
    >
      <div className="min-w-0">
        <p className="truncate font-mono text-sm text-slate-700">
          {line.stockNo || '—'}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {line.fromWarehouseLabel || '—'}
        </p>
      </div>
      {showMaterialStack ? (
        <div
          className="min-w-0"
          onClick={(event) => event.stopPropagation()}
        >
          <MaterialNameStack name={line.mtrName} code={line.mtrNo} />
        </div>
      ) : null}
      <div
        className="min-w-0"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <SelectCore
          id={unitSelectId}
          options={unitSelectOptions}
          value={line.unitId || ''}
          onChange={handleUnitChange}
          isDisabled={unitDisabled}
          noOptionsMessage={
            !mtrId ? '請先選擇庫存' : '此物料尚無可選單位'
          }
          placeholder="單位"
          errors={unitErrors}
          hideErrorMessage
          aria-label={`單位 ${index + 1}`}
          aria-invalid={unitInvalid ? true : undefined}
          title={unitInvalid ? '請選擇單位' : undefined}
          className="w-full"
        />
      </div>
      <div
        className="min-w-0"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <FloatingInput
          id={qtyId}
          label={qtyTip}
          type="number"
          min={1}
          max={qtyMax > 0 ? qtyMax : undefined}
          step={1}
          value={qtyRaw === '' || qtyRaw == null ? '' : String(qtyRaw)}
          onChange={(event) => {
            onQtyChange?.(index, event.target.value, { finalize: false });
          }}
          onBlur={(event) => {
            onQtyChange?.(index, event.target.value, {
              finalize: true,
              maxQty: qtyClampMax,
            });
          }}
          errors={qtyErrors}
          suppressInlineError
          containerClassName="mb-0! w-full min-w-[13rem]"
        />
      </div>
      <div
        className="min-w-0"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <SelectCore
          id={`wo-transfer-policy-${line.invStockId}-${index}`}
          options={STOCK_NO_POLICY_OPTIONS}
          value={policy}
          onChange={(next) => onStockNoPolicyChange?.(index, next)}
          isDisabled={isFullTransfer}
          hideErrorMessage
          placeholder="庫編設定"
          aria-label={`庫編設定 ${index + 1}`}
          className="w-full"
        />
      </div>
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
          aria-label={`移除 ${line.stockNo || '明細'}`}
          onClick={(event) => {
            event.stopPropagation();
            onRemove(index);
          }}
        >
          <PiTrashBold className="size-4" aria-hidden />
        </Button>
      </div>
    </li>
  );
}

DraftLineRow.propTypes = {
  line: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  showMaterialStack: PropTypes.bool,
  onFocusMaterial: PropTypes.func,
  onRemove: PropTypes.func.isRequired,
  onQtyChange: PropTypes.func,
  onUnitChange: PropTypes.func,
  onUnitOptionsChange: PropTypes.func,
  onStockNoPolicyChange: PropTypes.func,
  groupMtrId: PropTypes.string,
};

function DraftMaterialGroup({
  group,
  open,
  highlighted,
  onToggleOpen,
  onFocusMaterial,
  onRemove,
  onQtyChange,
  onUnitChange,
  onUnitOptionsChange,
  onStockNoPolicyChange,
  groupRef,
}) {
  const totalQty = group.lines.reduce(
    (sum, item) => sum + (Number(item.line.qty) || 0),
    0,
  );
  const showMaterialStack = Boolean(group.isOtherGroup);

  return (
    <li
      ref={groupRef}
      className={cn(
        'overflow-hidden rounded-md border border-slate-200 transition-colors',
        highlighted && 'border-primary-400 bg-primary-50 ring-2 ring-primary-200',
      )}
    >
      <div className={draftGroupHeaderBarClassName({ open, highlighted })}>
        <button
          type="button"
          className="
            flex min-w-0 flex-1 items-center gap-2 text-left transition-colors
            hover:text-primary-600
          "
          aria-expanded={open}
          onClick={onToggleOpen}
        >
          <ChevronDown
            size={18}
            className={cn(
              'shortcut_transition-transform shrink-0 origin-center text-gray-600',
              open ? 'rotate-0' : '-rotate-90',
            )}
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-slate-800">
              {group.mtrName || '（未命名）'}
            </p>
            {group.isOtherGroup ? null : (
              <p className="truncate text-xs text-muted-foreground">
                {group.mtrNo || group.mtrId}
              </p>
            )}
          </div>
        </button>
        <div className="flex shrink-0 flex-wrap gap-3 text-xs text-muted-foreground">
          <span>
            已選
            {' '}
            {group.lines.length}
          </span>
          <span>
            操作總量
            {' '}
            {totalQty}
          </span>
        </div>
      </div>

      <div
        className={cn(
          'grid shortcut_transition-disclosure',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
        aria-hidden={!open}
      >
        <div className="min-h-0 overflow-hidden overflow-x-auto">
          <div className={cn('m-0 list-none p-0', LINES_MIN_W)}>
            <DraftLineHeader showMaterialStack={showMaterialStack} />
            <ul className="m-0 list-none divide-y divide-slate-300 p-0">
              {group.lines.map(({ line, index }) => (
                <DraftLineRow
                  key={line.clientLineId || `${line.invStockId || 'stock'}-${index}`}
                  line={line}
                  index={index}
                  showMaterialStack={showMaterialStack}
                  onFocusMaterial={onFocusMaterial}
                  onRemove={onRemove}
                  onQtyChange={onQtyChange}
                  onUnitChange={onUnitChange}
                  onUnitOptionsChange={onUnitOptionsChange}
                  onStockNoPolicyChange={onStockNoPolicyChange}
                  groupMtrId={group.mtrId}
                />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </li>
  );
}

DraftMaterialGroup.propTypes = {
  group: PropTypes.shape({
    mtrId: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    isOtherGroup: PropTypes.bool,
    lines: PropTypes.arrayOf(PropTypes.shape({
      line: PropTypes.object,
      index: PropTypes.number,
    })),
  }).isRequired,
  open: PropTypes.bool.isRequired,
  highlighted: PropTypes.bool,
  onToggleOpen: PropTypes.func.isRequired,
  onFocusMaterial: PropTypes.func,
  onRemove: PropTypes.func.isRequired,
  onQtyChange: PropTypes.func,
  onUnitChange: PropTypes.func,
  onUnitOptionsChange: PropTypes.func,
  onStockNoPolicyChange: PropTypes.func,
  groupRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
};

const WorkOrderTransferDraftLines = ({
  lines = [],
  onRemove,
  onFocusMaterial,
  onQtyChange,
  onUnitChange,
  onUnitOptionsChange,
  onStockNoPolicyChange,
  collapsedByMtrId = {},
  onToggleGroup,
  focusedMtrId = null,
  focusToken = 0,
}) => {
  const [filterKeyword, setFilterKeyword] = useState('');
  const groupRefs = useRef({});

  const groups = useMemo(() => {
    const order = [];
    const map = new Map();
    const otherLines = [];

    lines.forEach((line, index) => {
      if (line.isOtherMaterial) {
        otherLines.push({ line, index });
        return;
      }
      const mtrId = line.mtrId || `unknown-${index}`;
      if (!map.has(mtrId)) {
        map.set(mtrId, {
          mtrId,
          mtrNo: line.mtrNo,
          mtrName: line.mtrName,
          lines: [],
        });
        order.push(mtrId);
      }
      map.get(mtrId).lines.push({ line, index });
    });

    const result = order.map((mtrId) => map.get(mtrId));
    if (otherLines.length > 0) {
      result.push({
        mtrId: OTHER_MATERIAL_GROUP_KEY,
        mtrNo: '',
        mtrName: OTHER_MATERIAL_GROUP_NAME,
        isOtherGroup: true,
        lines: otherLines,
      });
    }
    return result;
  }, [lines]);

  const filteredGroups = useMemo(
    () => filterDraftGroupsByKeyword(groups, filterKeyword),
    [groups, filterKeyword],
  );

  useEffect(() => {
    if (!focusedMtrId) {
      return;
    }
    const node = groupRefs.current[focusedMtrId];
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [focusedMtrId, focusToken]);

  function isGroupOpen(mtrId) {
    return collapsedByMtrId[mtrId] !== true;
  }

  function handleRemove(index) {
    if (lines.length <= 1) {
      setFilterKeyword('');
    }
    onRemove(index);
  }

  const hasLines = lines.length > 0;

  return (
    <>
      <div className="pb-3 flex min-w-0 items-center gap-1.5 sticky top-0 left-0 bg-panel z-10">
        <TableSearchInput
          useKeyword
          keyword={filterKeyword}
          onKeywordChange={setFilterKeyword}
          placeholder="篩選料號、名稱、庫存編號、其他物料"
          className="min-w-0 w-full max-w-md flex-1 text-foreground"
          disabled={!hasLines}
        />
        {!hasLines ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="
                  inline-flex shrink-0 items-center border-0 bg-transparent p-0
                  text-gray-400 cursor-help
                  hover:text-primary-600
                "
                aria-label="篩選說明"
              >
                <HelpCircle size={14} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              請先從左側加入明細後再篩選
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>

      {!hasLines ? (
        <p className="form-section__empty-state">
          尚未加入明細。請自左欄開啟選庫。
        </p>
      ) : filteredGroups.length === 0 ? (
        <p className="form-section__empty-state">
          找不到符合關鍵字的明細。
        </p>
      ) : (
        <ul className="m-0 list-none space-y-3 p-0">
          {filteredGroups.map((group) => (
            <DraftMaterialGroup
              key={group.mtrId}
              group={group}
              open={isGroupOpen(group.mtrId)}
              highlighted={focusedMtrId === group.mtrId}
              onToggleOpen={() => onToggleGroup?.(group.mtrId)}
              onFocusMaterial={onFocusMaterial}
              onRemove={handleRemove}
              onQtyChange={onQtyChange}
              onUnitChange={onUnitChange}
              onUnitOptionsChange={onUnitOptionsChange}
              onStockNoPolicyChange={onStockNoPolicyChange}
              groupRef={(node) => {
                if (node) {
                  groupRefs.current[group.mtrId] = node;
                } else {
                  delete groupRefs.current[group.mtrId];
                }
              }}
            />
          ))}
        </ul>
      )}
    </>
  );
};

WorkOrderTransferDraftLines.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.shape({
    mtrId: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    invStockId: PropTypes.string,
    stockNo: PropTypes.string,
    fromWarehouseLabel: PropTypes.string,
    qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    availableQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    unitId: PropTypes.string,
    unitOptions: PropTypes.array,
    stockNoPolicy: PropTypes.string,
    isOtherMaterial: PropTypes.bool,
  })),
  onRemove: PropTypes.func.isRequired,
  onFocusMaterial: PropTypes.func,
  onQtyChange: PropTypes.func,
  onUnitChange: PropTypes.func,
  onUnitOptionsChange: PropTypes.func,
  onStockNoPolicyChange: PropTypes.func,
  collapsedByMtrId: PropTypes.objectOf(PropTypes.bool),
  onToggleGroup: PropTypes.func,
  focusedMtrId: PropTypes.string,
  focusToken: PropTypes.number,
};

export default WorkOrderTransferDraftLines;
